import { useState, useEffect } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Testimonial } from '@common';
import { Button, Form, Input, Modal, Upload, Rate } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import './styles.css';

const { TextArea } = Input;

interface AddTestimonialModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (testimonial: any) => void;
  initialValues?: Testimonial;
}

export const AddTestimonialModal = ({ isOpen, onCancel, onSave, initialValues }: AddTestimonialModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setAvatarFile(null);

      if (initialValues) {
        const formValues = {
          ...initialValues,
          stars: initialValues.stars || 5,
        };
        form.setFieldsValue(formValues);
      }
    }
  }, [isOpen, initialValues, form]);

  const handleAvatarUpload = (file: UploadFile) => {
    setAvatarFile(file as unknown as File);
    return false; // Prevent automatic upload
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const testimonialData = {
        name: values.name,
        description: values.description,
        text: values.text,
        stars: values.stars || 5,
        isActive: true, // Always set to true for new testimonials
        avatar: avatarFile,
      };

      onSave(testimonialData);
      form.resetFields();
      setAvatarFile(null);

    } catch (error) {
      console.error('Error validating form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAvatarFile(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <span className="font-orator text-orangicide text-xl">
          {initialValues ? 'EDIT TESTIMONIAL' : 'ADD NEW TESTIMONIAL'}
        </span>
      }
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={700}
      styles={{
        mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.7)' },
        content: { backgroundColor: '#181717', border: '1px solid #3f3f46', borderRadius: '0px' },
        header: { backgroundColor: '#181717', borderBottom: '1px solid #3f3f46' },
      }}
      className="testimonial-modal mb-10"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="name"
          label={<span className="font-orator text-white">CLIENT NAME</span>}
          rules={[
            { required: true, message: 'Please enter the client name' },
            { min: 2, message: 'Name must be at least 2 characters' },
            { max: 100, message: 'Name must not exceed 100 characters' }
          ]}
          className="!mt-6"
        >
          <Input 
            className="bg-zinc-800 font-orator border-zinc-700 text-white" 
            placeholder="Ex: David Thompson" 
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={<span className="font-orator text-white">JOB TITLE / COMPANY</span>}
          rules={[
            { max: 200, message: 'Description must not exceed 200 characters' }
          ]}
        >
          <Input 
            className="bg-zinc-800 font-orator border-zinc-700 text-white" 
            placeholder="Ex: Thompson Development Group" 
          />
        </Form.Item>

        <Form.Item
          name="text"
          label={<span className="font-orator text-white">TESTIMONIAL TEXT</span>}
          rules={[
            { required: true, message: 'Please enter the testimonial text' },
            { min: 10, message: 'Testimonial must be at least 10 characters' },
            { max: 1000, message: 'Testimonial must not exceed 1000 characters' }
          ]}
        >
          <TextArea
            rows={4}
            className="bg-zinc-800 font-orator border-zinc-700 text-white"
            placeholder="Enter the client's testimonial..."
            showCount
            maxLength={1000}
            style={{ resize: 'none' }}
          />
        </Form.Item>

        <div className="flex gap-4 mb-4">
          <Form.Item
            name="stars"
            label={<span className="font-orator text-white">RATING</span>}
            initialValue={5}
            className="flex-1"
          >
            <Rate 
              className="!text-[#F74902]"
              allowHalf={false}
              count={5}
            />
          </Form.Item>

          <Form.Item
            name="avatar"
            label={<span className="font-orator text-white">CLIENT AVATAR</span>}
            className="flex-1"
          >
            <Upload
              name="avatar"
              listType="text"
              accept="image/*"
              beforeUpload={handleAvatarUpload}
              maxCount={1}
              showUploadList={{
                showRemoveIcon: true,
              }}
            >
              <Button 
                icon={<UploadOutlined />}
                className="btn-orangicide font-orator"
              >
                CHOOSE AVATAR
              </Button>
            </Upload>
          </Form.Item>
        </div>

        <div className="flex justify-end gap-4 pt-6 border-t border-zinc-700">
          <Button
            onClick={handleCancel}
            className="btn-orangicide font-orator text-black"
          >
            CANCEL
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            className="btn-orangicide font-orator text-black"
          >
            {initialValues ? 'UPDATE' : 'SAVE'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}; 